import moment from "moment"
import { OverlayTrigger, Popover, Table } from "react-bootstrap"
import type {
	I_AmortizationPeriod,
	I_VehicleLease,
	I_VehicleLeaseCalculationResponse,
} from "src/common_interfaces/I_VehicleLease"
import Loader from "src/components/common/Loader"
import { fmtUSD } from "../../fleetUtility"

type Props = {
	lease: I_VehicleLease
	calculations: I_VehicleLeaseCalculationResponse | null
	loading?: boolean
	compareTo?: {
		lease: I_VehicleLease
		calculations: I_VehicleLeaseCalculationResponse | null
	}
}

/**
 * Returns a highlight style if `current` differs from `previous`.
 * Returns undefined (no styling) if `previous` is undefined — this means
 * the consumer didn't pass a compareTo, so we render normally.
 */
const diffStyle = (
	current: unknown,
	previous: unknown | undefined,
): React.CSSProperties | undefined =>
	previous !== undefined && current !== previous
		? { backgroundColor: "#fde2e2" }
		: undefined

// Sums the operands, or returns null if any is missing — so a dropped field
// renders as "—" rather than a misleading total.
const sumOrNull = (...vals: Array<number | null | undefined>): number | null =>
	vals.some((v) => v == null)
		? null
		: vals.reduce((a, b) => (a ?? 0) + (b ?? 0), 0)

/**
 * Popover explaining how the capital lease test percentage was derived, using the
 * exact component values the calculation engine produced (no recomputation here).
 * Numerator = PV of future payments + PV of residual; denominator = fair market
 * value (Capitalized + Down Payment + Gain on Prior Vehicle); pass threshold is 90%.
 */
const capitalLeaseTestPopover = (
	lease: I_VehicleLease,
	calculations: I_VehicleLeaseCalculationResponse,
) => {
	const numerator = sumOrNull(
		calculations.PVOfFuturePayments,
		calculations.PVOfResidualValue,
	)
	const denominator = sumOrNull(
		lease.CapitalizedAmount,
		lease.DownPayment,
		lease.GainOnPriorVehicle,
	)
	return (
		<Popover id="capital-lease-test-explanation">
			<Popover.Title as="h3">Capital Lease Test</Popover.Title>
			<Popover.Content>
				<div>
					(PV of Payments + PV of Residual) / (Capitalized + Down Payment + Gain
					on Prior Vehicle) &ge; 90%
				</div>
				<hr />
				<div>PV of Payments: {fmtUSD(calculations.PVOfFuturePayments)}</div>
				<div>PV of Residual: {fmtUSD(calculations.PVOfResidualValue)}</div>
				<div>Capitalized Amount: {fmtUSD(lease.CapitalizedAmount)}</div>
				<div>Down Payment: {fmtUSD(lease.DownPayment)}</div>
				<div>Gain on Prior Vehicle: {fmtUSD(lease.GainOnPriorVehicle)}</div>
				<hr />
				<div>
					({fmtUSD(numerator)}) / ({fmtUSD(denominator)}) ={" "}
					{calculations.CapitalLeaseTestPercentage == null
						? "—"
						: `${(calculations.CapitalLeaseTestPercentage * 100).toFixed(2)}%`}
				</div>
				<div style={{ fontWeight: "bold" }}>
					{calculations.MeetCapitalLeaseTest
						? "Meets the 90% threshold"
						: "Does not meet the 90% threshold"}
				</div>
			</Popover.Content>
		</Popover>
	)
}

export default function LeaseInformationView({
	lease,
	calculations,
	loading,
	compareTo,
}: Props) {
	if (!lease) return null

	const prevLease = compareTo?.lease
	const prevCalc = compareTo?.calculations

	return (
		<>
			<div className="lease-details-side-by-side">
				{/* ========== Lease Details — stored input fields ========== */}
				<div className="lease-details-left">
					<h5>Lease Details (input values)</h5>
					<Table striped bordered size="sm">
						<tbody>
							<tr>
								<td>Unit Number</td>
								<td style={diffStyle(lease.UnitNumber, prevLease?.UnitNumber)}>
									{lease.UnitNumber}
								</td>
							</tr>
							<tr>
								<td>State</td>
								<td style={diffStyle(lease.State, prevLease?.State)}>
									{lease.State}
								</td>
							</tr>
							<tr>
								<td>Status</td>
								<td style={diffStyle(lease.Status, prevLease?.Status)}>
									{lease.Status}
								</td>
							</tr>
							{lease.Status === "Terminated" && (
								<tr style={{ color: "#d9534f" }}>
									<td>Termination Date</td>
									<td
										style={diffStyle(
											lease.LeaseRetirementDate,
											prevLease?.LeaseRetirementDate,
										)}
									>
										{moment(lease.LeaseRetirementDate).format("MM/DD/YYYY")}
									</td>
								</tr>
							)}
							<tr>
								<td>Date Received</td>
								<td
									style={diffStyle(lease.DateReceived, prevLease?.DateReceived)}
								>
									{moment(lease.DateReceived).format("MM/DD/YYYY")}
								</td>
							</tr>
							<tr>
								<td>Lease Length</td>
								<td
									style={diffStyle(lease.LeaseLength, prevLease?.LeaseLength)}
								>
									{lease.LeaseLength} months
								</td>
							</tr>
							<tr>
								<td>Month of First Payment</td>
								<td
									style={diffStyle(
										lease.MonthOfFirstPayment,
										prevLease?.MonthOfFirstPayment,
									)}
								>
									{moment(lease.MonthOfFirstPayment).format("MM/DD/YYYY")}
								</td>
							</tr>
							<tr>
								<td>Capitalized Amount</td>
								<td
									style={diffStyle(
										lease.CapitalizedAmount,
										prevLease?.CapitalizedAmount,
									)}
								>
									${lease.CapitalizedAmount?.toLocaleString()}
								</td>
							</tr>
							<tr>
								<td>Depreciation Rate (%)</td>
								<td
									style={diffStyle(
										lease.LeaseDepreciationRate,
										prevLease?.LeaseDepreciationRate,
									)}
								>
									{lease.LeaseDepreciationRate * 100}%
								</td>
							</tr>
							<tr>
								<td>Interest Rate (%)</td>
								<td
									style={diffStyle(lease.InterestRate, prevLease?.InterestRate)}
								>
									{lease.InterestRate * 100}%
								</td>
							</tr>
							<tr>
								<td>Management Fee %</td>
								<td
									style={diffStyle(
										lease.MgmtFeePercent,
										prevLease?.MgmtFeePercent,
									)}
								>
									{lease.MgmtFeePercent * 100}%
								</td>
							</tr>
							<tr>
								<td>Maintenance Program Monthly</td>
								<td
									style={diffStyle(
										lease.MaintenanceProgramMonthly,
										prevLease?.MaintenanceProgramMonthly,
									)}
								>
									${lease.MaintenanceProgramMonthly?.toLocaleString()}
								</td>
							</tr>
							<tr>
								<td>Sales Tax Per Month</td>
								<td
									style={diffStyle(
										lease.SalesTaxPerMonth,
										prevLease?.SalesTaxPerMonth,
									)}
								>
									${lease.SalesTaxPerMonth?.toLocaleString()}
								</td>
							</tr>
							<tr>
								<td>Sales Tax On Incentive</td>
								<td
									style={diffStyle(
										lease.SalesTaxOnIncentive,
										prevLease?.SalesTaxOnIncentive,
									)}
								>
									${lease.SalesTaxOnIncentive?.toLocaleString()}
								</td>
							</tr>
							<tr>
								<td>Period Zero Lease Expense</td>
								<td
									style={diffStyle(
										lease.PeriodZeroLeaseExpense,
										prevLease?.PeriodZeroLeaseExpense,
									)}
								>
									${lease.PeriodZeroLeaseExpense?.toLocaleString()}
								</td>
							</tr>
							<tr>
								<td>Period Zero Depreciation</td>
								<td
									style={diffStyle(
										lease.PeriodZeroDepreciation,
										prevLease?.PeriodZeroDepreciation,
									)}
								>
									${lease.PeriodZeroDepreciation?.toLocaleString()}
								</td>
							</tr>
							<tr>
								<td>End of Lease Service Charge</td>
								<td
									style={diffStyle(
										lease.EndOfLeaseServiceCharge,
										prevLease?.EndOfLeaseServiceCharge,
									)}
								>
									${lease.EndOfLeaseServiceCharge?.toLocaleString()}
								</td>
							</tr>
							<tr>
								<td>EFM Negotiated Rate</td>
								<td
									style={diffStyle(
										lease.EFMNegotiatedRate,
										prevLease?.EFMNegotiatedRate,
									)}
								>
									${lease.EFMNegotiatedRate?.toLocaleString()}
								</td>
							</tr>
							<tr>
								<td>Down Payment</td>
								<td
									style={diffStyle(lease.DownPayment, prevLease?.DownPayment)}
								>
									${lease.DownPayment?.toLocaleString()}
								</td>
							</tr>
							<tr>
								<td>Gain On Prior Vehicle</td>
								<td
									style={diffStyle(
										lease.GainOnPriorVehicle,
										prevLease?.GainOnPriorVehicle,
									)}
								>
									${lease.GainOnPriorVehicle?.toLocaleString()}
								</td>
							</tr>
						</tbody>
					</Table>
				</div>

				{/* ========== Calculated Values — derived from the lease calculation engine ========== */}
				{loading && !calculations && (
					<div className="lease-details-right">
						<h5>Calculated Values</h5>
						<Loader type="infinite" title="Loading calculations..." />
					</div>
				)}
				{calculations && (
					<div className="lease-details-right">
						<h5>Calculated Values</h5>
						<Table striped bordered size="sm">
							<tbody>
								<tr>
									<td>Depreciation</td>
									<td
										style={diffStyle(
											calculations.Depreciation,
											prevCalc?.Depreciation,
										)}
									>
										$
										{calculations.Depreciation?.toLocaleString(undefined, {
											minimumFractionDigits: 2,
											maximumFractionDigits: 2,
										})}
									</td>
								</tr>
								<tr>
									<td>Interest Expense</td>
									<td
										style={diffStyle(
											calculations.InterestExpense,
											prevCalc?.InterestExpense,
										)}
									>
										$
										{calculations.InterestExpense?.toLocaleString(undefined, {
											minimumFractionDigits: 2,
											maximumFractionDigits: 2,
										})}
									</td>
								</tr>
								<tr>
									<td>Management Fee</td>
									<td
										style={diffStyle(
											calculations.ManagementFee,
											prevCalc?.ManagementFee,
										)}
									>
										$
										{calculations.ManagementFee?.toLocaleString(undefined, {
											minimumFractionDigits: 2,
											maximumFractionDigits: 2,
										})}
									</td>
								</tr>
								<tr>
									<td style={{ fontWeight: "bold" }}>Total Monthly Payment</td>
									<td
										style={diffStyle(
											calculations.TotalMonthlyLeasePayment,
											prevCalc?.TotalMonthlyLeasePayment,
										)}
									>
										$
										{calculations.TotalMonthlyLeasePayment?.toLocaleString(
											undefined,
											{
												minimumFractionDigits: 2,
												maximumFractionDigits: 2,
											},
										)}
									</td>
								</tr>
								<tr>
									<td style={{ fontWeight: "bold" }}>Residual Value</td>
									<td
										style={diffStyle(
											calculations.EndOfLeaseResidualValue,
											prevCalc?.EndOfLeaseResidualValue,
										)}
									>
										$
										{calculations.EndOfLeaseResidualValue?.toLocaleString(
											undefined,
											{
												minimumFractionDigits: 2,
												maximumFractionDigits: 2,
											},
										)}
									</td>
								</tr>
								<tr>
									<td>PV of Future Payments</td>
									<td
										style={diffStyle(
											calculations.PVOfFuturePayments,
											prevCalc?.PVOfFuturePayments,
										)}
									>
										$
										{calculations.PVOfFuturePayments?.toLocaleString(
											undefined,
											{
												minimumFractionDigits: 2,
												maximumFractionDigits: 2,
											},
										)}
									</td>
								</tr>
								<tr>
									<td>
										Capital Lease Test{" "}
										<OverlayTrigger
											trigger={["hover", "focus"]}
											placement="right"
											overlay={capitalLeaseTestPopover(lease, calculations)}
										>
											<button
												type="button"
												className="material-icons"
												aria-label="How the capital lease test is calculated"
												style={{
													background: "none",
													border: "none",
													padding: 0,
													cursor: "help",
													fontSize: "1rem",
													verticalAlign: "middle",
												}}
											>
												info_outline
											</button>
										</OverlayTrigger>
									</td>
									<td
										style={diffStyle(
											calculations.CapitalLeaseTestPercentage,
											prevCalc?.CapitalLeaseTestPercentage,
										)}
									>
										{(calculations.CapitalLeaseTestPercentage * 100)?.toFixed(
											2,
										)}
										%
									</td>
								</tr>
								<tr>
									<td style={{ fontWeight: "bold" }}>
										Meets Capital Lease Test
									</td>
									<td
										style={diffStyle(
											calculations.MeetCapitalLeaseTest,
											prevCalc?.MeetCapitalLeaseTest,
										)}
									>
										{calculations.MeetCapitalLeaseTest ? "Yes" : "No"}
									</td>
								</tr>
							</tbody>
						</Table>
					</div>
				)}
			</div>

			{/* ========== Amortization Schedule — period-by-period ASC 842 schedule with journal entries ========== */}
			{/* Not diffed: row-by-row schedule comparison is mostly noise when inputs change. */}
			<div>
				{loading && !calculations && (
					<div>
						<h5>Amortization Schedule</h5>
						<Loader type="infinite" title="Loading amortization schedule..." />
					</div>
				)}
				{calculations && (
					<div>
						<h5>Amortization Schedule</h5>
						<Table striped bordered hover size="sm">
							<thead>
								<tr style={{ textAlign: "center" }}>
									<th colSpan={10} />
									<th colSpan={2}>Amort Asset</th>
									<th colSpan={3}>Amort Liab & Rcrd Int</th>
									<th colSpan={2} className="table-section-gap">
										Depreciation
									</th>
									<th colSpan={2} />
								</tr>
								<tr>
									<th className="period-date-repeat">Period</th>
									<th className="period-date-repeat">Date</th>
									<th>Cash Payment</th>
									<th>ROU Asset Amort.</th>
									<th>Opening Liability</th>
									<th>Imputed Interest</th>
									<th>Interest Days</th>
									<th>Liability Amort.</th>
									<th>Net Liability</th>
									<th>Net ROU Asset</th>
									<th>(dr) ROU Asset Amort Exp</th>
									<th>(cr) Right of Use Asset</th>
									<th>(dr) Lease Liability</th>
									<th>(dr) Interest Exp</th>
									<th>(cr) Cash</th>
									<th className="table-section-gap">Mo Depr</th>
									<th>Net Value</th>
									<th className="period-date-repeat">Period</th>
									<th className="period-date-repeat">Date</th>
								</tr>
							</thead>
							<tbody>
								{calculations.AmortizationSchedule?.map(
									(period: I_AmortizationPeriod) => (
										<tr
											key={period.PeriodNumber}
											className={
												period.PeriodNumber === -1 ? "termination-row" : ""
											}
										>
											<td className="period-date-repeat">
												{period.PeriodNumber === -1
													? "TRMINTE"
													: period.PeriodNumber}
											</td>
											<td className="period-date-repeat">
												{moment(period.Date).format("MM/DD/YY")}
											</td>
											<td>
												$
												{period.Payment?.toLocaleString(undefined, {
													minimumFractionDigits: 2,
													maximumFractionDigits: 2,
												})}
											</td>
											<td>
												{period.PeriodNumber === -1
													? "$0.00"
													: `$${period.ROUAssetAmortization?.toLocaleString(
															undefined,
															{
																minimumFractionDigits: 2,
																maximumFractionDigits: 2,
															},
														)}`}
											</td>
											<td>
												$
												{period.OpeningLeaseLiability?.toLocaleString(
													undefined,
													{
														minimumFractionDigits: 2,
														maximumFractionDigits: 2,
													},
												)}
											</td>
											<td>
												$
												{period.ImputedInterest?.toLocaleString(undefined, {
													minimumFractionDigits: 2,
													maximumFractionDigits: 2,
												})}
											</td>
											<td>{period.InterestDays}</td>
											<td>
												$(
												{period.LeaseLiabilityAmortization?.toLocaleString(
													undefined,
													{
														minimumFractionDigits: 2,
														maximumFractionDigits: 2,
													},
												)}
												)
											</td>
											<td>
												$
												{period.NetLeaseLiability?.toLocaleString(undefined, {
													minimumFractionDigits: 2,
													maximumFractionDigits: 2,
												})}
											</td>
											<td>
												$
												{period.NetROUAsset?.toLocaleString(undefined, {
													minimumFractionDigits: 2,
													maximumFractionDigits: 2,
												})}
											</td>
											<td>
												{period.PeriodNumber === -1
													? "$0.00"
													: `$${period.ROUAssetAmortization?.toLocaleString(
															undefined,
															{
																minimumFractionDigits: 2,
																maximumFractionDigits: 2,
															},
														)}`}
											</td>
											<td>
												$
												{period.ROUAssetAmortization?.toLocaleString(
													undefined,
													{
														minimumFractionDigits: 2,
														maximumFractionDigits: 2,
													},
												)}
											</td>
											<td>
												$
												{period.LeaseLiabilityAmortization?.toLocaleString(
													undefined,
													{
														minimumFractionDigits: 2,
														maximumFractionDigits: 2,
													},
												)}
											</td>
											<td>
												$
												{period.ImputedInterest?.toLocaleString(undefined, {
													minimumFractionDigits: 2,
													maximumFractionDigits: 2,
												})}
											</td>
											<td>
												$
												{period.Payment?.toLocaleString(undefined, {
													minimumFractionDigits: 2,
													maximumFractionDigits: 2,
												})}
											</td>
											<td className="table-section-gap">
												$
												{period.MonthlyDepreciation?.toLocaleString(undefined, {
													minimumFractionDigits: 2,
													maximumFractionDigits: 2,
												})}
											</td>
											<td>
												$
												{period.NetResidualValue?.toLocaleString(undefined, {
													minimumFractionDigits: 2,
													maximumFractionDigits: 2,
												})}
											</td>
											<td className="period-date-repeat">
												{period.PeriodNumber === -1
													? "TRMINTE"
													: period.PeriodNumber}
											</td>
											<td className="period-date-repeat">
												{moment(period.Date).format("MM/DD/YY")}
											</td>
										</tr>
									),
								)}
							</tbody>
						</Table>
					</div>
				)}
			</div>
		</>
	)
}
